import React, { useId, useState } from 'react';
import styled from 'styled-components';
import { Icon, IconName } from '../ui/Icon';

interface MetricCardProps {
  iconName: IconName;
  title: string;
  value: string;
  unit?: string;
  delta: string | undefined;
  deltaValue?: number | null;
  showComparison?: boolean;
  improvementDirection?: 'increase' | 'decrease';
  muted?: boolean;
  className?: string;
  tooltipMessage?: string | undefined;
}
const Card = styled.div<{ $muted: boolean }>`
  height: 100%;
  min-width: 0;
  padding: 1.25rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface);
  opacity: ${({ $muted }) => ($muted ? 0.7 : 1)};
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: var(--text-muted);
  h2 {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-secondary);
  }
`;
const Value = styled.div`
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
  small {
    font-size: 0.8125rem;
    font-weight: 400;
    letter-spacing: 0;
    color: var(--text-muted);
  }
`;
const Delta = styled.div<{ $improved: boolean | undefined }>`
  min-height: 1.25rem;
  margin-top: 0.75rem;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: ${({ $improved }) =>
    $improved === undefined
      ? 'var(--text-muted)'
      : $improved
        ? 'var(--success-600)'
        : 'var(--danger-600)'};
`;
const HelpButton = styled.button`
  margin-left: auto;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  background: transparent;
  color: var(--text-muted);
  border: 0;
  border-radius: var(--radius-md);
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid var(--interactive-focus);
  }
  @media (max-width: 767px) {
    width: 44px;
    height: 44px;
  }
`;
const HelpText = styled.p`
  margin: 0.75rem 0 0;
  font-size: 0.8125rem;
  color: var(--text-muted);
  line-height: 1.5;
`;

export const MetricCard: React.FC<MetricCardProps> = ({
  iconName,
  title,
  value,
  unit,
  delta,
  deltaValue,
  improvementDirection,
  showComparison = true,
  muted = false,
  className,
  tooltipMessage,
}) => {
  const [helpOpen, setHelpOpen] = useState(false);
  const helpId = useId();
  const hasDelta = deltaValue != null && Number.isFinite(deltaValue);
  const improved =
    hasDelta && deltaValue !== 0 && improvementDirection
      ? improvementDirection === 'increase'
        ? deltaValue > 0
        : deltaValue < 0
      : undefined;
  return (
    <Card $muted={muted} className={className}>
      <Header>
        <Icon name={iconName} size="sm" aria-hidden />
        <h2>{title}</h2>
        {tooltipMessage && (
          <HelpButton
            type="button"
            aria-label={`Información sobre ${title}`}
            aria-expanded={helpOpen}
            aria-controls={helpId}
            onClick={() => setHelpOpen(!helpOpen)}
          >
            <Icon name="info" size="sm" aria-hidden />
          </HelpButton>
        )}
      </Header>
      <Value>
        {value}
        {unit && (
          <>
            {' '}
            <small>{unit}</small>
          </>
        )}
      </Value>
      {showComparison && (
        <Delta
          $improved={improved}
          role="group"
          aria-label={
            improved === undefined
              ? 'Comparación'
              : improved
                ? 'Mejora'
                : 'Retroceso'
          }
        >
          {hasDelta && deltaValue !== 0 && (
            <Icon
              name={deltaValue > 0 ? 'arrow-up' : 'arrow-down'}
              size="xs"
              aria-hidden
            />
          )}
          {hasDelta
            ? delta
            : delta && delta !== '— vs mes pasado'
              ? delta
              : 'Sin comparación disponible'}
        </Delta>
      )}
      {tooltipMessage && (
        <HelpText id={helpId} hidden={!helpOpen}>
          {tooltipMessage}
        </HelpText>
      )}
    </Card>
  );
};
export default MetricCard;
